import React, { useEffect, useState } from 'react'

import { cn } from '@/lib/utils.ts'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { Input } from '@/components/ui/input'
import { AdaptiveTextarea } from '@/components/adaptive-textarea.tsx'
import {
  addProperty,
  toggleRequired,
} from '@/components/json-schema-generator/schema-utils.tsx'
import { PropertySchema } from '@/components/json-schema-generator/types.ts'
import { TagsInput } from '@/components/tags-input.tsx'

import { TypeSelector } from './type-selector.tsx'

interface PropertyFormProps {
  name: string
  property: PropertySchema
  path: string[]
  onUpdate: (path: string[], property: PropertySchema) => void
  onRemove: (path: string[]) => void
  onRename: (oldPath: string[], newName: string) => void
  parent?: PropertySchema
}

export function PropertyForm({
  name,
  property,
  path,
  onUpdate,
  onRemove,
  onRename,
  parent,
}: PropertyFormProps) {
  const [newPropertyName, setNewPropertyName] = useState('')
  const [propertyName, setPropertyName] = useState(name)
  const [description, setDescription] = useState(property.description || '')
  const [enumValues, setEnumValues] = useState<Array<string>>([])
  const [examples, setExamples] = useState<Array<string>>([])

  useEffect(() => {
    setPropertyName(name)
    setDescription(property.description || '')
    // @ts-expect-error TODO
    setEnumValues(property.enum)
    // @ts-expect-error TODO
    setExamples(property.examples)
  }, [name, property])

  const handleTypeChange = (type: Pick<PropertySchema, 'type' | 'format'>) => {
    onUpdate(path, { ...property, ...type })
  }

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const newDescription = e.target.value
    setDescription(newDescription)

    onUpdate(path, { ...property, description: newDescription })
  }

  const handleEnumChange = (values: PropertySchema['enum']) => {
    if (!values) return

    onUpdate(path, {
      ...property,
      enum: values,
    })
  }

  const handleExamplesChange = (values: PropertySchema['examples']) => {
    if (!values) return

    onUpdate(path, {
      ...property,
      examples: values,
    })
  }

  const handleAddProperty = () => {
    if (newPropertyName) {
      const newProperty = addProperty(
        { type: 'object', properties: property.properties || {}, required: [] },
        newPropertyName,
        'string'
      )
      onUpdate(path, newProperty)
      setNewPropertyName('')
    }
  }

  const handleAddArrayItem = () => {
    onUpdate(path, { ...property, items: { type: 'string' } })
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPropertyName(e.target.value)
  }

  const handleNameBlur = () => {
    if (propertyName !== name) {
      onRename(path, propertyName)
    }
  }

  const handleRequiredChange = (isRequired: boolean) => {
    const update = toggleRequired(parent!, path, isRequired)
    onUpdate(path.slice(0, -1), update)
  }

  const nestingLevel = path.length
  const borderColor = [
    cn('border-blue-200'),
    cn('border-green-200'),
    cn('border-yellow-200'),
    cn('border-red-200'),
    cn('border-purple-200'),
  ][nestingLevel % 5]

  return (
    <div
      className={`mt-4 rounded-lg shadow-sm ${nestingLevel > 1 && `ml-${nestingLevel * 4} ${borderColor} border-l-4 p-4`}`}
    >
      {/*path: {JSON.stringify(path)}*/}
      <span className="mb-2 inline-block text-xs text-muted-foreground">
        {path.join(' / ')}
      </span>
      <div className="mb-4 flex flex-wrap items-center gap-4">
        {parent?.type !== 'array' && (
          <div className="grow">
            <label
              htmlFor={`property-name-${path.join('-')}`}
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Nome
            </label>
            <Input
              id={`property-name-${path.join('-')}`}
              value={propertyName}
              onChange={handleNameChange}
              onBlur={handleNameBlur}
            />
          </div>
        )}
        <div>
          <label
            htmlFor={`property-type-${path.join('-')}`}
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Tipologia dato
          </label>
          <TypeSelector
            id={`property-type-${path.join('-')}`}
            schema={property}
            onChange={handleTypeChange}
          />
        </div>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center space-x-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onRemove(path)}
          >
            Elimina
          </Button>
          <label className="flex items-center text-sm">
            <Checkbox
              className="mr-2"
              checked={parent?.required?.includes(name)}
              onCheckedChange={handleRequiredChange}
            />
            Campo obbligatorio
          </label>
        </div>
      </div>
      <div className="space-y-4">
        <div>
          <label
            htmlFor={`property-description-${path.join('-')}`}
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Descrizione
          </label>
          <AdaptiveTextarea
            id={`property-description-${path.join('-')}`}
            value={description}
            onChange={handleDescriptionChange}
            rows={1}
          />
        </div>

        {(property.type === 'string' ||
          property.type === 'number' ||
          property.type === 'integer') && (
          <div>
            <label
              htmlFor={`property-enum-${path.join('-')}`}
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Valori consentiti
            </label>
            <TagsInput value={enumValues} onChange={handleEnumChange} />
          </div>
        )}

        <div>
          <label
            htmlFor={`property-examples-${path.join('-')}`}
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Valori di esempio
          </label>

          <TagsInput value={examples} onChange={handleExamplesChange} />
        </div>
      </div>
      {property.type === 'object' && (
        <div className="mt-6 border-t border-gray-200 pt-4">
          <h4 className="mb-2 text-sm font-semibold">Campi subordinati</h4>
          <div className="space-y-4">
            {Object.entries(property.properties || {}).map(
              ([subName, subProperty]) => (
                <PropertyForm
                  key={subName}
                  name={subName}
                  property={subProperty as PropertySchema}
                  path={[...path, subName]}
                  onUpdate={onUpdate}
                  onRemove={onRemove}
                  onRename={onRename}
                  parent={property}
                />
              )
            )}
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Input
              placeholder="Nuovo campo"
              value={newPropertyName}
              onChange={(e) => setNewPropertyName(e.target.value)}
            />
            <Button type="button" onClick={handleAddProperty}>
              Aggiungi Campo
            </Button>
          </div>
        </div>
      )}
      {property.type === 'array' && (
        <div className="mt-6 border-t border-gray-200 pt-4">
          <h4 className="mb-2 text-sm font-semibold">Lista elementi</h4>
          {property.items ? (
            <PropertyForm
              name="items"
              property={property.items as PropertySchema}
              path={[...path, 'items']}
              onUpdate={(updatedPath, updatedItem) => {
                onUpdate(updatedPath, { ...updatedItem })
              }}
              onRemove={() => onUpdate(path, { ...property, items: undefined })}
              onRename={onRename}
              parent={property}
            />
          ) : (
            <Button type="button" onClick={handleAddArrayItem}>
              Add Array Item
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
